<script>
  import Icon from "@iconify/svelte";
  import { t, translate } from "./lib/i18n";

  import settingsIcon from "@iconify/icons-mdi/cog-outline";
  import clockIcon from "@iconify/icons-mdi/clock-outline";
  import timerIcon from "@iconify/icons-mdi/timer-outline";
  import alertIcon from "@iconify/icons-mdi/alert-circle-outline";
  import tagIcon from "@iconify/icons-mdi/tag-outline";

  export let apiBase = ""; // NEW (kept for parity / future use)
  export let apiKey = "";  // NEW

  export let setup = null;
  export let setupErr = "";

  function envValue(name) {
    const env = setup?.env || [];
    const hit = env.find((e) => e.name === name);
    return hit?.value || hit?.default || "";
  }

  function item(label, value, hint = "") {
    return { label, value, hint };
  }

  function section(title, items) {
    return { title, items };
  }

  function pretty(v) {
    const s = (v ?? "").toString().trim();
    return s ? s : "—";
  }

  $: meta = setup?.meta || null;
  $: cfg = setup?.config || {};

  function cfgValue(path, fallback = "") {
    const parts = String(path || "")
      .split(".")
      .map((p) => p.trim())
      .filter(Boolean);
    let cur = cfg;
    for (const p of parts) {
      if (cur && typeof cur === "object" && p in cur) {
        cur = cur[p];
      } else {
        return fallback;
      }
    }
    return cur ?? fallback;
  }

  $: sections = [
    section($t('settings.sec_ui'), [
      item($t('settings.ui_enabled'), pretty(envValue("UI_ENABLED"))),
      item($t('settings.ui_dist_dir'), pretty(envValue("UI_DIST_DIR"))),
      item($t('settings.ui_auth_username'), pretty(envValue("PHOTRON_USERNAME"))),
      item($t('settings.ui_auth_password'), envValue("PHOTRON_PASSWORD") ? $t('common.set') : $t('common.not_set'))
    ]),

    section($t('settings.sec_api_auth'), [
      item($t('settings.shared_api_key'), envValue("SHARED_API_KEY") ? $t('common.set') : $t('common.not_set')),
      item($t('settings.shared_api_secret'), envValue("SHARED_API_SECRET") ? $t('common.set') : $t('common.not_set'))
    ]),

    section($t('settings.sec_core_worker'), [
      item($t('settings.worker_id'), pretty(envValue("WORKER_ID")), $t('settings.defaults_to_hostname')),
      item($t('settings.max_workers'), pretty(envValue("MAX_WORKERS"))),
      item($t('settings.job_timeout'), pretty(envValue("JOB_TIMEOUT"))),
      item($t('settings.http_port'), pretty(envValue("HTTP_PORT")))
    ]),

    section($t('settings.sec_queue'), [
      item($t('settings.backend'), pretty(envValue("QUEUE_BACKEND"))),
      item($t('settings.redis_address'), pretty(envValue("REDIS_ADDR"))),
      item($t('settings.redis_password'), envValue("REDIS_PASSWORD") ? $t('common.set') : $t('common.not_set')),
      item($t('settings.input_queue'), pretty(envValue("REDIS_INPUT_QUEUE"))),
      item($t('settings.results_queue'), pretty(envValue("REDIS_RESULTS_QUEUE"))),
      item($t('settings.results_ttl'), pretty(envValue("REDIS_RESULTS_TTL"))),
      item($t('settings.processing_ttl'), pretty(envValue("REDIS_PROCESSING_TTL"))),
      item($t('settings.reaper_interval'), pretty(envValue("REDIS_REAPER_INTERVAL")))
    ]),

    section($t('settings.sec_storage'), [
      item($t('settings.backend'), pretty(envValue("STORAGE_BACKEND")), $t('settings.hint_storage_backend')),
      item($t('settings.s3_endpoint'), pretty(envValue("S3_ENDPOINT"))),
      item($t('settings.s3_region'), pretty(envValue("S3_REGION"))),
      item($t('settings.use_ssl'), pretty(envValue("S3_USE_SSL"))),
      item($t('settings.force_path_style'), pretty(envValue("S3_FORCE_PATH_STYLE"))),
      item($t('settings.access_key_id'), envValue("S3_ACCESS_KEY_ID") ? $t('common.set') : $t('common.not_set')),
      item($t('settings.secret_access_key'), envValue("S3_SECRET_ACCESS_KEY") ? $t('common.set') : $t('common.not_set')),
      item($t('settings.input_bucket'), pretty(envValue("S3_INPUT_BUCKET"))),
      item($t('settings.output_bucket'), pretty(envValue("S3_OUTPUT_BUCKET"))),
      item($t('settings.input_prefix'), pretty(envValue("S3_INPUT_PREFIX"))),
      item($t('settings.output_prefix'), pretty(envValue("S3_OUTPUT_PREFIX")))
    ]),

    section($t('settings.sec_ffmpeg_core'), [
      item($t('settings.ffmpeg_bin'), pretty(envValue("FFMPEG_BIN"))),
      item($t('settings.ffprobe_bin'), pretty(envValue("FFPROBE_BIN"))),
      item($t('settings.direct_threshold'), pretty(envValue("DIRECT_THRESHOLD"))),
      item($t('settings.max_ffmpeg_processes'), pretty(envValue("MAX_FFMPEG_PROCESSES")))
    ]),

    section($t('settings.sec_video_encoding'), [
      item($t('settings.video_codec'), pretty(envValue("FFMPEG_VIDEO_CODEC"))),
      item($t('settings.preset'), pretty(envValue("FFMPEG_VIDEO_PRESET"))),
      item($t('settings.profile'), pretty(envValue("FFMPEG_VIDEO_PROFILE"))),
      item($t('settings.level'), pretty(envValue("FFMPEG_VIDEO_LEVEL"))),
      item($t('settings.pixel_format'), pretty(envValue("FFMPEG_VIDEO_PIX_FMT"))),
      item($t('settings.bitrate'), pretty(envValue("FFMPEG_VIDEO_BITRATE"))),
      item($t('settings.max_bitrate'), pretty(envValue("FFMPEG_VIDEO_MAXRATE"))),
      item($t('settings.buffer_size'), pretty(envValue("FFMPEG_VIDEO_BUFSIZE"))),
      item($t('settings.crf'), pretty(envValue("FFMPEG_VIDEO_CRF"))),
      item($t('settings.gop'), pretty(envValue("FFMPEG_VIDEO_GOP"))),
      item($t('settings.keyint_min'), pretty(envValue("FFMPEG_VIDEO_KEYINT_MIN"))),
      item($t('settings.scene_threshold'), pretty(envValue("FFMPEG_VIDEO_SC_THRESHOLD")))
    ]),

    section($t('settings.sec_audio_encoding'), [
      item($t('settings.audio_codec'), pretty(envValue("FFMPEG_AUDIO_CODEC"))),
      item($t('settings.bitrate'), pretty(envValue("FFMPEG_AUDIO_BITRATE"))),
      item($t('settings.sample_rate'), pretty(envValue("FFMPEG_AUDIO_SAMPLERATE"))),
      item($t('settings.channels'), pretty(envValue("FFMPEG_AUDIO_CHANNELS")))
    ]),

    section($t('settings.sec_hls_output'), [
      item($t('settings.enabled'), pretty(envValue("FFMPEG_HLS_ENABLED"))),
      item($t('settings.segment_duration'), pretty(envValue("FFMPEG_HLS_SEGMENT_DURATION"))),
      item($t('settings.segment_filename'), pretty(envValue("FFMPEG_HLS_SEGMENT_FILENAME"))),
      item($t('settings.playlist_name'), pretty(envValue("FFMPEG_HLS_OUTPUT_NAME"))),
      item($t('settings.playlist_type'), pretty(envValue("FFMPEG_HLS_PLAYLIST_TYPE"))),
      item($t('settings.flags'), pretty(envValue("FFMPEG_HLS_FLAGS")))
    ]),

    section($t('settings.sec_mp4_output'), [
      item($t('settings.enabled'), pretty(envValue("FFMPEG_MP4_ENABLED"))),
      item($t('settings.output_name'), pretty(envValue("FFMPEG_MP4_OUTPUT_NAME"))),
      item($t('settings.fast_start'), pretty(envValue("FFMPEG_MP4_FASTSTART")))
    ]),

    section($t('settings.sec_thumbnails'), [
      item($t('settings.enabled'), pretty(envValue("FFMPEG_THUMB_ENABLED"))),
      item($t('settings.interval_seconds'), pretty(envValue("FFMPEG_THUMB_INTERVAL_SECONDS"))),
      item($t('settings.count'), pretty(envValue("FFMPEG_THUMB_COUNT"))),
      item($t('settings.offset_seconds'), pretty(envValue("FFMPEG_THUMB_OFFSET_SECONDS"))),
      item($t('settings.width'), pretty(envValue("FFMPEG_THUMB_WIDTH"))),
      item($t('settings.height'), pretty(envValue("FFMPEG_THUMB_HEIGHT"))),
      item($t('settings.quality'), pretty(envValue("FFMPEG_THUMB_QUALITY"))),
      item($t('settings.format'), pretty(envValue("FFMPEG_THUMB_FORMAT"))),
      item($t('settings.output_name'), pretty(envValue("FFMPEG_THUMB_OUTPUT_NAME"))),
      item($t('settings.scene_detection'), pretty(envValue("FFMPEG_THUMB_SCENE_DETECTION"))),
      item($t('settings.scene_threshold'), pretty(envValue("FFMPEG_THUMB_SCENE_THRESHOLD")))
    ]),

    section($t('settings.sec_variants'), [
      item($t('settings.720p_enabled'), pretty(envValue("FFMPEG_720P_ENABLED"))),
      item($t('settings.720p_height'), pretty(envValue("FFMPEG_720P_HEIGHT"))),
      item($t('settings.720p_bitrate'), pretty(envValue("FFMPEG_720P_BITRATE"))),
      item($t('settings.720p_maxrate'), pretty(envValue("FFMPEG_720P_MAXRATE"))),
      item($t('settings.720p_bufsize'), pretty(envValue("FFMPEG_720P_BUFSIZE"))),
      item($t('settings.720p_crf'), pretty(envValue("FFMPEG_720P_CRF"))),

      item($t('settings.480p_enabled'), pretty(envValue("FFMPEG_480P_ENABLED"))),
      item($t('settings.480p_height'), pretty(envValue("FFMPEG_480P_HEIGHT"))),
      item($t('settings.480p_bitrate'), pretty(envValue("FFMPEG_480P_BITRATE"))),
      item($t('settings.480p_maxrate'), pretty(envValue("FFMPEG_480P_MAXRATE"))),
      item($t('settings.480p_bufsize'), pretty(envValue("FFMPEG_480P_BUFSIZE"))),
      item($t('settings.480p_crf'), pretty(envValue("FFMPEG_480P_CRF"))),
      item($t('settings.480p_profile'), pretty(envValue("FFMPEG_480P_PROFILE"))),
      item($t('settings.480p_level'), pretty(envValue("FFMPEG_480P_LEVEL"))),

      item($t('settings.360p_enabled'), pretty(envValue("FFMPEG_360P_ENABLED"))),
      item($t('settings.360p_height'), pretty(envValue("FFMPEG_360P_HEIGHT"))),
      item($t('settings.360p_bitrate'), pretty(envValue("FFMPEG_360P_BITRATE"))),
      item($t('settings.360p_maxrate'), pretty(envValue("FFMPEG_360P_MAXRATE"))),
      item($t('settings.360p_bufsize'), pretty(envValue("FFMPEG_360P_BUFSIZE"))),
      item($t('settings.360p_crf'), pretty(envValue("FFMPEG_360P_CRF"))),
      item($t('settings.360p_profile'), pretty(envValue("FFMPEG_360P_PROFILE"))),
      item($t('settings.360p_level'), pretty(envValue("FFMPEG_360P_LEVEL")))
    ]),

    section($t('settings.sec_alt_codecs'), [
      item($t('settings.vp9_enabled'), pretty(envValue("FFMPEG_VP9_ENABLED"))),
      item($t('settings.vp9_height'), pretty(envValue("FFMPEG_VP9_HEIGHT"))),
      item($t('settings.vp9_crf'), pretty(envValue("FFMPEG_VP9_CRF"))),
      item($t('settings.vp9_deadline'), pretty(envValue("FFMPEG_VP9_DEADLINE"))),
      item($t('settings.vp9_cpu_used'), pretty(envValue("FFMPEG_VP9_CPU_USED"))),
      item($t('settings.vp9_row_mt'), pretty(envValue("FFMPEG_VP9_ROW_MT"))),
      item($t('settings.vp9_audio_codec'), pretty(envValue("FFMPEG_VP9_AUDIO_CODEC"))),
      item($t('settings.vp9_audio_bitrate'), pretty(envValue("FFMPEG_VP9_AUDIO_BITRATE"))),
      item($t('settings.vp9_output'), pretty(envValue("FFMPEG_VP9_OUTPUT_NAME"))),

      item($t('settings.av1_enabled'), pretty(envValue("FFMPEG_AV1_ENABLED"))),
      item($t('settings.av1_height'), pretty(envValue("FFMPEG_AV1_HEIGHT"))),
      item($t('settings.av1_crf'), pretty(envValue("FFMPEG_AV1_CRF"))),
      item($t('settings.av1_preset'), pretty(envValue("FFMPEG_AV1_PRESET"))),
      item($t('settings.av1_audio_codec'), pretty(envValue("FFMPEG_AV1_AUDIO_CODEC"))),
      item($t('settings.av1_audio_bitrate'), pretty(envValue("FFMPEG_AV1_AUDIO_BITRATE"))),
      item($t('settings.av1_output'), pretty(envValue("FFMPEG_AV1_OUTPUT_NAME"))),

      item($t('settings.ogv_enabled'), pretty(envValue("FFMPEG_OGV_ENABLED"))),
      item($t('settings.ogv_height'), pretty(envValue("FFMPEG_OGV_HEIGHT"))),
      item($t('settings.ogv_quality'), pretty(envValue("FFMPEG_OGV_QUALITY"))),
      item($t('settings.ogv_audio_codec'), pretty(envValue("FFMPEG_OGV_AUDIO_CODEC"))),
      item($t('settings.ogv_audio_quality'), pretty(envValue("FFMPEG_OGV_AUDIO_QUALITY"))),
      item($t('settings.ogv_output'), pretty(envValue("FFMPEG_OGV_OUTPUT_NAME")))
    ]),

    section($t('settings.sec_legacy'), [
      item($t('settings.legacy_paths_enabled'), pretty(envValue("LEGACY_PATHS_ENABLED")))
    ]),

    section($t('settings.sec_resource_limits'), [
      item($t('settings.max_input_bytes'), pretty(envValue("MAX_INPUT_BYTES"))),
      item($t('settings.max_output_bytes'), pretty(envValue("MAX_OUTPUT_BYTES"))),
      item($t('settings.min_free_disk_bytes'), pretty(envValue("MIN_FREE_DISK_BYTES"))),
      item($t('settings.output_overhead_factor'), pretty(envValue("OUTPUT_OVERHEAD_FACTOR"))),
      item($t('settings.max_duration_seconds'), pretty(envValue("MAX_DURATION_SECONDS"))),
      item($t('settings.max_width'), pretty(envValue("MAX_WIDTH"))),
      item($t('settings.max_height'), pretty(envValue("MAX_HEIGHT"))),
      item($t('settings.telemetry_interval'), pretty(envValue("TELEMETRY_INTERVAL"))),
      item($t('settings.cpu_alert_pct'), pretty(envValue("CPU_ALERT_PERCENT"))),
      item($t('settings.mem_alert_pct'), pretty(envValue("MEM_ALERT_PERCENT"))),
      item($t('settings.disk_alert_pct'), pretty(envValue("DISK_ALERT_PERCENT")))
    ]),

    section($t('settings.sec_webhooks'), [
      item($t('settings.default_webhooks'), pretty(envValue("DEFAULT_WEBHOOKS"))),
      item($t('settings.timeout'), pretty(envValue("WEBHOOK_TIMEOUT"))),
      item($t('settings.max_retry'), pretty(envValue("WEBHOOK_MAX_RETRY"))),
      item($t('settings.retry_min_wait'), pretty(envValue("WEBHOOK_RETRY_MIN_WAIT"))),
      item($t('settings.retry_max_wait'), pretty(envValue("WEBHOOK_RETRY_MAX_WAIT"))),
      item($t('settings.secret'), envValue("WEBHOOK_SECRET") ? $t('common.set') : $t('common.not_set'))
    ]),

    section($t('settings.sec_startup_checks'), [
      item($t('settings.aws_identity_check'), pretty(envValue("STARTUP_AWS_IDENTITY_CHECK"))),
      item($t('settings.s3_list_check'), pretty(envValue("STARTUP_S3_LIST_CHECK"))),
      item($t('settings.ffmpeg_codec_check'), pretty(envValue("STARTUP_FFMPEG_CODEC_CHECK")))
    ]),

    section($t('settings.sec_resolved_core'), [
      item($t('settings.worker_id'), pretty(cfgValue("worker_id"))),
      item($t('settings.hostname'), pretty(cfgValue("hostname"))),
      item($t('settings.max_workers'), pretty(cfgValue("max_workers"))),
      item($t('settings.job_timeout'), pretty(cfgValue("job_timeout"))),
      item($t('settings.http_port'), pretty(cfgValue("http_port"))),
      item($t('settings.queue_backend'), pretty(cfgValue("queue_backend"))),
      item($t('settings.ui_enabled'), pretty(cfgValue("ui_enabled"))),
      item($t('settings.ui_dist_dir'), pretty(cfgValue("ui_dist_dir"))),
      item($t('settings.api_auth_enabled'), pretty(cfgValue("api_auth_enabled")))
    ]),

    section($t('settings.sec_resolved_redis'), [
      item($t('settings.redis_addr'), pretty(cfgValue("redis_addr"))),
      item($t('settings.input_queue'), pretty(cfgValue("redis_input_queue"))),
      item($t('settings.results_queue'), pretty(cfgValue("redis_results_queue"))),
      item($t('settings.results_ttl'), pretty(cfgValue("redis_results_ttl"))),
      item($t('settings.processing_ttl'), pretty(cfgValue("redis_processing_ttl"))),
      item($t('settings.reaper_interval'), pretty(cfgValue("redis_reaper_interval")))
    ]),

    section($t('settings.sec_resolved_storage'), [
      item($t('settings.backend'), pretty(cfgValue("storage_backend"))),
      item($t('settings.endpoint'), pretty(cfgValue("s3_endpoint"))),
      item($t('settings.region'), pretty(cfgValue("s3_region"))),
      item($t('settings.use_ssl'), pretty(cfgValue("s3_use_ssl"))),
      item($t('settings.force_path_style'), pretty(cfgValue("s3_force_path_style"))),
      item($t('settings.input_bucket'), pretty(cfgValue("s3_input_bucket"))),
      item($t('settings.output_bucket'), pretty(cfgValue("s3_output_bucket"))),
      item($t('settings.input_prefix'), pretty(cfgValue("s3_input_prefix"))),
      item($t('settings.output_prefix'), pretty(cfgValue("s3_output_prefix")))
    ]),

    section($t('settings.sec_resolved_ffmpeg_core'), [
      item($t('settings.bin'), pretty(cfgValue("ffmpeg.bin"))),
      item($t('settings.probe_bin'), pretty(cfgValue("ffmpeg.probe_bin"))),
      item($t('settings.direct_threshold'), pretty(cfgValue("ffmpeg.direct_threshold"))),
      item($t('settings.max_processes'), pretty(cfgValue("ffmpeg.max_ffmpeg_processes"))),
      item($t('settings.legacy_paths'), pretty(cfgValue("ffmpeg.legacy_paths_enabled")))
    ]),

    section($t('settings.sec_resolved_video'), [
      item($t('settings.codec'), pretty(cfgValue("ffmpeg.video.codec"))),
      item($t('settings.preset'), pretty(cfgValue("ffmpeg.video.preset"))),
      item($t('settings.profile'), pretty(cfgValue("ffmpeg.video.profile"))),
      item($t('settings.level'), pretty(cfgValue("ffmpeg.video.level"))),
      item($t('settings.pixel_format'), pretty(cfgValue("ffmpeg.video.pix_fmt"))),
      item($t('settings.bitrate'), pretty(cfgValue("ffmpeg.video.bitrate"))),
      item($t('settings.maxrate'), pretty(cfgValue("ffmpeg.video.maxrate"))),
      item($t('settings.bufsize'), pretty(cfgValue("ffmpeg.video.bufsize"))),
      item($t('settings.crf'), pretty(cfgValue("ffmpeg.video.crf"))),
      item($t('settings.gop'), pretty(cfgValue("ffmpeg.video.gop"))),
      item($t('settings.keyint_min'), pretty(cfgValue("ffmpeg.video.keyint_min"))),
      item($t('settings.scene_threshold'), pretty(cfgValue("ffmpeg.video.sc_threshold")))
    ]),

    section($t('settings.sec_resolved_audio'), [
      item($t('settings.codec'), pretty(cfgValue("ffmpeg.audio.codec"))),
      item($t('settings.bitrate'), pretty(cfgValue("ffmpeg.audio.bitrate"))),
      item($t('settings.sample_rate'), pretty(cfgValue("ffmpeg.audio.sample_rate"))),
      item($t('settings.channels'), pretty(cfgValue("ffmpeg.audio.channels")))
    ]),

    section($t('settings.sec_resolved_hls'), [
      item($t('settings.enabled'), pretty(cfgValue("ffmpeg.hls.enabled"))),
      item($t('settings.segment_duration'), pretty(cfgValue("ffmpeg.hls.segment_duration"))),
      item($t('settings.segment_filename'), pretty(cfgValue("ffmpeg.hls.segment_filename"))),
      item($t('settings.output_name'), pretty(cfgValue("ffmpeg.hls.output_name"))),
      item($t('settings.playlist_type'), pretty(cfgValue("ffmpeg.hls.playlist_type"))),
      item($t('settings.flags'), pretty(cfgValue("ffmpeg.hls.flags")))
    ]),

    section($t('settings.sec_resolved_mp4'), [
      item($t('settings.enabled'), pretty(cfgValue("ffmpeg.mp4.enabled"))),
      item($t('settings.output_name'), pretty(cfgValue("ffmpeg.mp4.output_name"))),
      item($t('settings.fast_start'), pretty(cfgValue("ffmpeg.mp4.faststart")))
    ]),

    section($t('settings.sec_resolved_thumbnails'), [
      item($t('settings.enabled'), pretty(cfgValue("ffmpeg.thumb.enabled"))),
      item($t('settings.interval_seconds'), pretty(cfgValue("ffmpeg.thumb.interval_seconds"))),
      item($t('settings.count'), pretty(cfgValue("ffmpeg.thumb.count"))),
      item($t('settings.offset_seconds'), pretty(cfgValue("ffmpeg.thumb.offset_seconds"))),
      item($t('settings.width'), pretty(cfgValue("ffmpeg.thumb.width"))),
      item($t('settings.height'), pretty(cfgValue("ffmpeg.thumb.height"))),
      item($t('settings.quality'), pretty(cfgValue("ffmpeg.thumb.quality"))),
      item($t('settings.format'), pretty(cfgValue("ffmpeg.thumb.format"))),
      item($t('settings.output_name'), pretty(cfgValue("ffmpeg.thumb.output_name"))),
      item($t('settings.scene_detection'), pretty(cfgValue("ffmpeg.thumb.scene_detection"))),
      item($t('settings.scene_threshold'), pretty(cfgValue("ffmpeg.thumb.scene_threshold")))
    ]),

    section($t('settings.sec_resolved_variants'), [
      item($t('settings.variants'), pretty(JSON.stringify(cfgValue("ffmpeg.variants", []))))
    ]),

    section($t('settings.sec_resolved_alt_codecs'), [
      item($t('settings.alt_profiles'), pretty(JSON.stringify(cfgValue("ffmpeg.alt_profiles", {}))))
    ]),

    section($t('settings.sec_resolved_resources'), [
      item($t('settings.max_input_bytes'), pretty(cfgValue("resources.max_input_bytes"))),
      item($t('settings.max_output_bytes'), pretty(cfgValue("resources.max_output_bytes"))),
      item($t('settings.min_free_disk'), pretty(cfgValue("resources.min_free_disk_bytes"))),
      item($t('settings.output_overhead'), pretty(cfgValue("resources.output_overhead_factor"))),
      item($t('settings.max_duration'), pretty(cfgValue("resources.max_duration_seconds"))),
      item($t('settings.max_width'), pretty(cfgValue("resources.max_width"))),
      item($t('settings.max_height'), pretty(cfgValue("resources.max_height"))),
      item($t('settings.telemetry_interval'), pretty(cfgValue("resources.telemetry_interval"))),
      item($t('settings.cpu_alert_pct'), pretty(cfgValue("resources.cpu_alert_percent"))),
      item($t('settings.mem_alert_pct'), pretty(cfgValue("resources.mem_alert_percent"))),
      item($t('settings.disk_alert_pct'), pretty(cfgValue("resources.disk_alert_percent")))
    ])
  ];

  // No fetches in this component. We still accept apiKey so App can pass it consistently.
  // If auth is removed mid-session, App will swap to Login anyway.

  const mono =
    "font-mono text-xs text-zinc-200 whitespace-nowrap overflow-hidden text-ellipsis";

  const sectionCard =
    "rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5";

  const cell =
    "rounded-xl border border-white/10 bg-white/[0.03] p-3 min-w-0";

  const klabel = "text-xs text-zinc-400 flex flex-wrap items-center gap-2";
  const khint =
    "text-[11px] text-zinc-500 inline-flex items-center gap-1 whitespace-nowrap";

  const headerChip =
    "rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-zinc-300 inline-flex items-center gap-2";
</script>

<main class="w-full px-4 py-6">
  <section class="ui-card p-5 sm:p-6">
    <!-- Header -->
    <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div class="flex items-start gap-3">
        <span
          class="inline-flex h-9 w-9 items-center justify-center border border-amber-400/60 bg-amber-500/50 text-amber-50"
        >
          <Icon icon={settingsIcon} width="18" height="18" />
        </span>

        <div>
          <h1 class="text-lg font-normal tracking-wide text-zinc-100">
            {$t('settings.title')}
          </h1>
          <p class="mt-0.5 text-sm text-zinc-400">
            {$t('settings.subtitle')}
          </p>
        </div>
      </div>

      <div class="flex flex-wrap gap-2">
        <div class={headerChip} title={$t('common.base')}>
          <Icon icon={tagIcon} width="16" height="16" />
          <span class="text-zinc-400">{$t('common.base')}</span>
          <span class="font-medium text-zinc-200">{apiBase || $t('common.same_origin')}</span>
        </div>

        <div class={headerChip} title={$t('settings.api_key')}>
          <Icon icon={tagIcon} width="16" height="16" />
          <span class="text-zinc-400">{$t('settings.api_key')}</span>
          <span class="font-medium text-zinc-200">{apiKey ? $t('common.set') : $t('common.not_set')}</span>
        </div>

        {#if meta?.now_utc}
          <div class={headerChip} title={$t('settings.server_time')}>
            <Icon icon={clockIcon} width="16" height="16" />
            <span class="text-zinc-400">{$t('settings.server_time')}</span>
            <span class="font-medium text-zinc-200">{meta.now_utc}</span>
          </div>
        {/if}

        {#if meta?.uptime}
          <div class={headerChip} title={$t('settings.uptime')}>
            <Icon icon={timerIcon} width="16" height="16" />
            <span class="text-zinc-400">{$t('settings.uptime')}</span>
            <span class="font-medium text-zinc-200">{meta.uptime}</span>
          </div>
        {/if}
      </div>
    </div>

    <!-- Error / Loading -->
    {#if setupErr}
      <div
        class="mt-4 rounded-xl border border-rose-400/35 bg-rose-400/10 px-4 py-3 text-sm text-rose-100 flex items-center gap-2"
      >
        <Icon icon={alertIcon} width="18" height="18" />
        <span class="break-words">{setupErr}</span>
      </div>
    {/if}

    {#if !setup}
      <div
        class="mt-6 rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-zinc-400"
      >
        {$t('common.loading')}
      </div>
    {:else}
      <!-- Sections -->
      <div class="mt-6 grid grid-cols-1 gap-4">
        {#each sections as s (s.title)}
          <div class={sectionCard}>
            <div
              class="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3"
            >
              <div class="flex items-center gap-2">
                <Icon icon={tagIcon} width="16" height="16" />
                <div class="text-sm font-normal tracking-wide text-white">
                  {s.title}
                </div>
              </div>

              <div class="text-xs text-zinc-500">{s.items.length} {$t('common.items')}</div>
            </div>

            <div
              class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
            >
              {#each s.items as it (it.label)}
                <div class={cell}>
                  <div class={klabel}>
                    <span>{it.label}</span>
                    {#if it.hint}
                      <span class={khint}>• {it.hint}</span>
                    {/if}
                  </div>

                  <div class={`${mono} mt-2`} title={it.value}>
                    {it.value}
                  </div>
                </div>
              {/each}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </section>
</main>
